import React, { useState, useEffect, useCallback, useRef } from 'react';
import { businessAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './BusinessRegistrations.css';

const BusinessRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    processing: 0,
    active: 0,
    rejected: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    jurisdiction_id: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [jurisdictionDropdownOpen, setJurisdictionDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [jurisdictions, setJurisdictions] = useState([]);

  const filterDropdownRef = useRef(null);
  const jurisdictionDropdownRef = useRef(null);
  const statusDropdownRefs = useRef({});

  // Detail Modal State
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    registration: null,
    loading: false,
    error: null
  });

  // Poll loading state
  const [polling, setPolling] = useState(false);

  const fetchRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await businessAPI.getAll(filters);
      if (response.data.success) {
        setRegistrations(response.data.data.registrations);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await businessAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchJurisdictions = async () => {
    try {
      const response = await businessAPI.getJurisdictions();
      if (response.data.success) {
        setJurisdictions(response.data.data.jurisdictions || response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch jurisdictions:', error);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    fetchStats();
    fetchJurisdictions();
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (jurisdictionDropdownRef.current && !jurisdictionDropdownRef.current.contains(event.target)) {
        setJurisdictionDropdownOpen(false);
      }
      if (statusDropdownOpen !== null) {
        const currentRef = statusDropdownRefs.current[statusDropdownOpen];
        if (currentRef && !currentRef.contains(event.target)) {
          setStatusDropdownOpen(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status, page: 1 }));
  };

  const handleJurisdictionFilter = (jurisdictionId) => {
    setFilters(prev => ({ ...prev, jurisdiction_id: prev.jurisdiction_id === jurisdictionId ? '' : jurisdictionId, page: 1 }));
    setJurisdictionDropdownOpen(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleStatusChange = async (id, newStatus, notes = '') => {
    try {
      await businessAPI.updateStatus(id, { status: newStatus, notes });
      fetchRegistrations();
      fetchStats();
      setStatusDropdownOpen(null);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      setDetailModal({ isOpen: true, registration: null, loading: true, error: null });
      const response = await businessAPI.getById(id);
      if (response.data.success) {
        setDetailModal({
          isOpen: true,
          registration: response.data.data.registration,
          loading: false,
          error: null
        });
      } else {
        setDetailModal({ isOpen: true, registration: null, loading: false, error: 'Failed to load registration details' });
      }
    } catch (error) {
      console.error('Failed to fetch registration details:', error);
      setDetailModal({ isOpen: true, registration: null, loading: false, error: error.message || 'Failed to load details' });
    }
  };

  const handlePollStatus = async (id) => {
    try {
      setPolling(true);
      const response = await businessAPI.pollStatus(id);
      if (response.data.success && response.data.data.updated) {
        // Refresh the detail modal
        handleViewDetails(id);
        fetchRegistrations();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to poll status:', error);
    } finally {
      setPolling(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModal({ isOpen: false, registration: null, loading: false, error: null });
  };

  const statusOptions = [
    { value: 'draft', label: 'DRAFT', className: 'status-draft' },
    { value: 'pending_payment', label: 'PENDING PAYMENT', className: 'status-pending-payment' },
    { value: 'submitted', label: 'SUBMITTED', className: 'status-submitted' },
    { value: 'processing', label: 'PROCESSING', className: 'status-processing' },
    { value: 'pending_signature', label: 'PENDING SIGNATURE', className: 'status-pending-signature' },
    { value: 'pending_kyb', label: 'PENDING KYB', className: 'status-pending-kyb' },
    { value: 'pending_review', label: 'PENDING REVIEW', className: 'status-pending-review' },
    { value: 'waiting_activate', label: 'WAITING ACTIVATE', className: 'status-waiting-activate' },
    { value: 'active', label: 'ACTIVE', className: 'status-active' },
    { value: 'rejected', label: 'REJECTED', className: 'status-rejected' },
    { value: 'cancelled', label: 'CANCELLED', className: 'status-cancelled' }
  ];

  const filterOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_payment', label: 'Pending Payment' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'processing', label: 'Processing' },
    { value: 'pending_signature', label: 'Pending Signature' },
    { value: 'pending_kyb', label: 'Pending KYB' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'waiting_activate', label: 'Waiting Activate' },
    { value: 'active', label: 'Active' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'JUST NOW';
    if (hours < 24) return `${hours}H AGO`;
    return `${days}D AGO`;
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">BUSINESS REGISTRATIONS</h1>
            <p className="page-subtitle">COMPANY REGISTRATION TRACKING CENTER</p>
          </div>
          <div className="header-right">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by reg #, company, email..."
                value={filters.search}
                onChange={handleSearch}
              />
            </div>
            <div className="custom-dropdown" ref={filterDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              >
                <span>{filterOptions.find(opt => opt.value === filters.status)?.label || 'All Status'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {filterDropdownOpen && (
                <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.status === option.value ? 'active' : ''}`}
                      onClick={() => {
                        handleStatusFilter(option.value);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="custom-dropdown" ref={jurisdictionDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setJurisdictionDropdownOpen(!jurisdictionDropdownOpen)}
              >
                <span>
                  {jurisdictions.find(j => j.id === parseInt(filters.jurisdiction_id))?.name || 'All Jurisdictions'}
                </span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {jurisdictionDropdownOpen && (
                <div className="custom-dropdown-menu">
                  <div
                    className={`custom-dropdown-item ${filters.jurisdiction_id === '' ? 'active' : ''}`}
                    onClick={() => handleJurisdictionFilter('')}
                  >
                    All Jurisdictions
                  </div>
                  {jurisdictions.map((j) => (
                    <div
                      key={j.id}
                      className={`custom-dropdown-item ${filters.jurisdiction_id === j.id.toString() ? 'active' : ''}`}
                      onClick={() => handleJurisdictionFilter(j.id.toString())}
                    >
                      {j.name} ({j.country})
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid stats-grid-4">
            <div
              className={`stat-card stat-card-clickable ${filters.status === '' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL</span>
                <span className="stat-value">{stats.total}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-primary stat-card-clickable ${filters.status === 'processing' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('processing')}
            >
              <div className="stat-info">
                <span className="stat-label">PROCESSING</span>
                <span className="stat-value">{stats.processing}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${filters.status === 'active' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('active')}
            >
              <div className="stat-info">
                <span className="stat-label">ACTIVE</span>
                <span className="stat-value">{stats.active}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-error stat-card-clickable ${filters.status === 'rejected' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('rejected')}
            >
              <div className="stat-info">
                <span className="stat-label">REJECTED</span>
                <span className="stat-value">{stats.rejected}</span>
              </div>
            </div>
          </div>

          {/* Registrations Table */}
          <div className="dashboard-section biz-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading registrations...</p>
              </div>
            ) : registrations.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <p>No registrations found</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>REGISTRATION</th>
                        <th>COMPANY</th>
                        <th>PARTNER</th>
                        <th>JURISDICTION</th>
                        <th>AMOUNT</th>
                        <th>STATUS</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((reg) => (
                        <tr key={reg.id}>
                          <td>
                            <div className="application-info">
                              <div className="application-avatar biz-avatar">
                                {reg.jurisdiction_country === 'Honduras' ? 'H' : 'B'}
                              </div>
                              <div className="application-details">
                                <span className="application-number">{reg.registration_number}</span>
                                <span className="application-date">{formatDate(reg.created_at)}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="applicant-info">
                              <span className="applicant-name">{reg.company_name || reg.companyName || 'N/A'}</span>
                              <span className="applicant-email">{reg.user_email}</span>
                            </div>
                          </td>
                          <td>
                            <div className="applicant-info">
                              <span className="applicant-name">{reg.partner_name || '—'}</span>
                              <span className="applicant-email">{reg.partner_name ? 'via partner MCP' : 'direct'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="visa-info">
                              <span className="visa-country">{reg.jurisdiction_name}</span>
                              <span className="visa-type">{reg.jurisdiction_country}</span>
                            </div>
                          </td>
                          <td>
                            <div className="amount-info">
                              <span className="amount-value">{formatCurrency(reg.total_amount, reg.currency)}</span>
                              <span className={`payment-status payment-status-${reg.payment_status}`}>{reg.payment_status || 'pending'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="status-cell">
                              <div
                                className="status-custom-dropdown"
                                ref={el => statusDropdownRefs.current[reg.id] = el}
                              >
                                <button
                                  className={`status-dropdown-trigger status-dropdown-${reg.status}`}
                                  onClick={(e) => {
                                    if (statusDropdownOpen === reg.id) {
                                      setStatusDropdownOpen(null);
                                    } else {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setDropdownPosition({
                                        top: rect.bottom + 4,
                                        left: rect.left
                                      });
                                      setStatusDropdownOpen(reg.id);
                                    }
                                  }}
                                >
                                  <span>{getStatusConfig(reg.status).label}</span>
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                {statusDropdownOpen === reg.id && (
                                  <div
                                    className="status-dropdown-menu"
                                    style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                                  >
                                    {statusOptions.map((option) => (
                                      <div
                                        key={option.value}
                                        className={`status-dropdown-item ${reg.status === option.value ? 'active' : ''}`}
                                        onClick={() => {
                                          handleStatusChange(reg.id, option.value);
                                        }}
                                      >
                                        {option.label}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <span className="status-time">{formatRelativeTime(reg.updated_at)}</span>
                            </div>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-view-application"
                                onClick={() => handleViewDetails(reg.id)}
                              >
                                VIEW
                              </button>
                              {reg.provider_company_id && !['active', 'rejected', 'cancelled'].includes(reg.status) && (
                                <button
                                  className="btn-view-application"
                                  style={{ marginLeft: 6, opacity: polling ? 0.6 : 1 }}
                                  disabled={polling}
                                  onClick={() => handlePollStatus(reg.id)}
                                >
                                  {polling ? '...' : 'SYNC'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="table-footer">
                  <div className="pagination-info">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} registrations
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Detail Modal */}
      {detailModal.isOpen && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Registration Details</h2>
              <button className="modal-close" onClick={closeDetailModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {detailModal.loading ? (
              <div className="modal-body loading-state">
                <div className="spinner"></div>
                <p>Loading details...</p>
              </div>
            ) : detailModal.error ? (
              <div className="modal-body loading-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-error, #ef4444)" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p style={{ color: 'var(--color-error, #ef4444)' }}>{detailModal.error}</p>
                <button className="btn-view-application" onClick={closeDetailModal} style={{ marginTop: '16px' }}>
                  Close
                </button>
              </div>
            ) : detailModal.registration && (
              <div className="modal-body">
                {/* Poll Status Button */}
                <div className="biz-detail-actions">
                  <button
                    className="btn-poll-status"
                    onClick={() => handlePollStatus(detailModal.registration.id)}
                    disabled={polling}
                  >
                    {polling ? 'Polling...' : 'Poll Provider Status'}
                  </button>
                </div>

                <div className="detail-section">
                  <h3 className="detail-section-title">Registration Info</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Registration Number</span>
                      <span className="detail-value">{detailModal.registration.registration_number}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className={`detail-status status-badge-${detailModal.registration.status}`}>
                        {getStatusConfig(detailModal.registration.status).label}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Provider Status</span>
                      <span className="detail-value">{detailModal.registration.provider_status || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Provider Company ID</span>
                      <span className="detail-value">{detailModal.registration.provider_company_id || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Created</span>
                      <span className="detail-value">{formatDate(detailModal.registration.created_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Updated</span>
                      <span className="detail-value">{formatDate(detailModal.registration.updated_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3 className="detail-section-title">Jurisdiction</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Name</span>
                      <span className="detail-value">{detailModal.registration.jurisdiction_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Country</span>
                      <span className="detail-value">{detailModal.registration.jurisdiction_country}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Provider</span>
                      <span className="detail-value">{detailModal.registration.provider_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Blockchain</span>
                      <span className="detail-value">{detailModal.registration.chain || '-'}</span>
                    </div>
                  </div>
                </div>

                {detailModal.registration.company_data && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Company Info</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Legal Name</span>
                        <span className="detail-value">{detailModal.registration.company_data.legalName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Trading Name</span>
                        <span className="detail-value">{detailModal.registration.company_data.tradingName}</span>
                      </div>
                      {detailModal.registration.company_data.mainActivity && (
                        <div className="detail-item">
                          <span className="detail-label">Main Activity</span>
                          <span className="detail-value">{detailModal.registration.company_data.mainActivity}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {detailModal.registration.founder_data && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Founder Info</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Full Name</span>
                        <span className="detail-value">{detailModal.registration.founder_data.name}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Email</span>
                        <span className="detail-value">{detailModal.registration.founder_data.email}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Job Role</span>
                        <span className="detail-value">{detailModal.registration.founder_data.jobRole || 'Director'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {detailModal.registration.office_address && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Office Address</h3>
                    <div className="detail-grid">
                      <div className="detail-item detail-item-full">
                        <span className="detail-label">Address</span>
                        <span className="detail-value">
                          {detailModal.registration.office_address.line1}
                          {detailModal.registration.office_address.line2 ? `, ${detailModal.registration.office_address.line2}` : ''}
                          <br />
                          {detailModal.registration.office_address.city}
                          {detailModal.registration.office_address.state ? `, ${detailModal.registration.office_address.state}` : ''}{' '}
                          {detailModal.registration.office_address.postalCode}, {detailModal.registration.office_address.country}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <h3 className="detail-section-title">Payment Info</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Total Amount</span>
                      <span className="detail-value detail-value-highlight">
                        {formatCurrency(detailModal.registration.total_amount, detailModal.registration.currency)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Payment Status</span>
                      <span className={`detail-payment-status payment-${detailModal.registration.payment_status || 'pending'}`}>
                        {(detailModal.registration.payment_status || 'pending').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {detailModal.registration.signature_url && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Signature</h3>
                    <div className="detail-grid">
                      <div className="detail-item detail-item-full">
                        <span className="detail-label">Signature URL</span>
                        <a
                          href={detailModal.registration.signature_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="detail-link"
                        >
                          {detailModal.registration.signature_url}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {detailModal.registration.documents && detailModal.registration.documents.length > 0 && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Documents ({detailModal.registration.documents.length})</h3>
                    <div className="documents-list">
                      {detailModal.registration.documents.map((doc) => (
                        <div key={doc.id} className="document-item">
                          <div className="document-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                            </svg>
                          </div>
                          <div className="document-info">
                            <span className="document-name">
                              {doc.document_type.charAt(0).toUpperCase() + doc.document_type.slice(1)}
                            </span>
                            <span className="document-meta">{doc.file_name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailModal.registration.statusHistory && detailModal.registration.statusHistory.length > 0 && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">Status History</h3>
                    <div className="history-timeline">
                      {detailModal.registration.statusHistory.map((item, index) => (
                        <div key={index} className="history-item">
                          <div className={`history-dot status-dot-${item.new_status}`}></div>
                          <div className="history-content">
                            <div className="history-header">
                              <span className={`history-status status-badge-${item.new_status}`}>
                                {(item.new_status || '').replace(/_/g, ' ').toUpperCase()}
                              </span>
                              <span className="history-date">{formatDate(item.created_at)}</span>
                            </div>
                            {item.notes && (
                              <p className="history-remarks">{item.notes}</p>
                            )}
                            {item.changed_by && (
                              <p className="history-remarks">By: {item.changed_by}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailModal.registration.user_email && (
                  <div className="detail-section">
                    <h3 className="detail-section-title">User Info</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Email</span>
                        <span className="detail-value">{detailModal.registration.user_email}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Phone</span>
                        <span className="detail-value">{detailModal.registration.user_phone || '-'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessRegistrations;
